// src/services/stepUp.ts — shared admin money-op step-up (T-185).
// The server gate (ADMIN_STEPUP_ENABLED, fail-safe off) protects admin withdrawal
// approvals + role grants with the admin's own TOTP, sent as the `X-2FA-Code` header
// (the same path the deposit-account config already uses). Callers send a code only
// AFTER the server asks for one — see the reactive flow in useStepUp.

import {
  isStaffConfirmationAnswer,
  isStaffConfirmationError,
  isStaffConfirmationRequired,
} from "./staffConfirmation/staffConfirmationErrors";
import {
  isStaffConfirmationOn,
  noteConfirmationDemanded,
} from "./staffConfirmation/staffConfirmationState";

/** onError variant that receives the error so callers can react to a step-up challenge. */
export type ErrorCallback = (error?: unknown) => void;

/** The header the API reads the admin's TOTP from (requireTwoFactor.readTwoFactorCode). */
export const STEP_UP_HEADER = "X-2FA-Code";

/** Build the step-up header, or nothing when there's no code (→ unchanged request). */
export const stepUpHeaders = (
  code?: string
): Record<string, string> | undefined => {
  const trimmed = code?.trim();
  return trimmed ? { [STEP_UP_HEADER]: trimmed } : undefined;
};

/**
 * The header the api reads a staff confirmation from: a short-lived token from
 * 88ID that confirms ONE exact request (see services/staffConfirmation). It
 * replaces X-2FA-Code on that request; the two are never sent together.
 */
export const STAFF_CONFIRMATION_HEADER = "X-Staff-Confirmation";

/** The sibling of stepUpHeaders for a staff confirmation. */
export const confirmationHeaders = (
  confirmation?: string
): Record<string, string> | undefined => {
  const trimmed = confirmation?.trim();
  return trimmed ? { [STAFF_CONFIRMATION_HEADER]: trimmed } : undefined;
};

/**
 * Server error codes that mean "supply or retry the admin's 2FA code":
 * - TWO_FACTOR_REQUIRED   → 403, no code sent yet → prompt for one
 * - INVALID_TWO_FACTOR_CODE / TWO_FACTOR_INVALID → 400, bad/expired code → retry
 */
export const STEP_UP_REQUIRED_CODE = "TWO_FACTOR_REQUIRED";
export const STEP_UP_INVALID_CODES = [
  "INVALID_TWO_FACTOR_CODE",
  "TWO_FACTOR_INVALID",
] as const;
const STEP_UP_ERROR_CODES: readonly string[] = [
  STEP_UP_REQUIRED_CODE,
  ...STEP_UP_INVALID_CODES,
];

/** Read the server error `code` off an Error the axios interceptor annotated (see apiService). */
export const getErrorCode = (error: unknown): string | undefined => {
  if (typeof error === "object" && error !== null && "code" in error) {
    const { code } = error as { code?: unknown };
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
};

/** True when this error code is any step-up code (missing OR invalid). */
export const isStepUpErrorCode = (code?: string | null): boolean =>
  !!code && STEP_UP_ERROR_CODES.includes(code);

/** True when the server is asking for a code that wasn't supplied. */
export const isStepUpRequired = (error: unknown): boolean =>
  getErrorCode(error) === STEP_UP_REQUIRED_CODE;

/** True when a supplied code was wrong or expired. */
export const isStepUpCodeInvalid = (error: unknown): boolean => {
  const code = getErrorCode(error);
  return !!code && (STEP_UP_INVALID_CODES as readonly string[]).includes(code);
};

/**
 * The OPERATOR's own account has no authenticator at all — 400 TWO_FACTOR_NOT_ENABLED
 * from requireTwoFactor (middleware/requireTwoFactor.ts), which `moneyOpGate` mounts
 * unconditionally. Distinct from the two cases above, and the distinction matters
 * (ENG-550): TWO_FACTOR_REQUIRED means "send a code" and INVALID_TWO_FACTOR_CODE means
 * "send a better one", but this one means no code can ever satisfy the route until the
 * operator enrols. Telling them to retype it would be a lie.
 *
 * Deliberately NOT added to STEP_UP_ERROR_CODES: that list suppresses the shared 403
 * handling in apiService and the withdrawal services' error surfacing, and this is a
 * 400 on a different set of routes. Widening it would change behaviour well outside
 * this fix.
 */
export const TWO_FACTOR_NOT_ENROLLED_CODE = "TWO_FACTOR_NOT_ENABLED";

/** True when the operator has no authenticator enrolled at all. */
export const isTwoFactorNotEnrolled = (error: unknown): boolean =>
  getErrorCode(error) === TWO_FACTOR_NOT_ENROLLED_CODE;

/**
 * The OPERATOR has no authenticator, said the other way: 403
 * ADMIN_TWO_FACTOR_SETUP_REQUIRED from `adminStepUp` (middleware/adminMoneyGates.ts),
 * which the developer-portal and AI8 routes run always and about 35 money-approval
 * routes run once `admin.stepup_enabled` is armed. Same meaning as
 * TWO_FACTOR_NOT_ENABLED, different code and status.
 */
export const ADMIN_TWO_FACTOR_SETUP_REQUIRED_CODE =
  "ADMIN_TWO_FACTOR_SETUP_REQUIRED";

/** The server locked the operator's codes after repeated wrong ones. */
export const TWO_FACTOR_TOO_MANY_ATTEMPTS_CODE = "TWO_FACTOR_TOO_MANY_ATTEMPTS";

/**
 * THE detector for "set up your authenticator first" (ENG-1378): true for either
 * way the api says the operator has no factor, false for every other error. Every
 * layer and screen asks this one function; no screen compares the strings itself.
 *
 * Decided by `code` alone. The two codes are issued by nothing else, and the
 * shapes that reach this function differ in what else they carry: the shared
 * interceptor's Error has `status`, `moneyOpRequest`'s ApiError may not, and the
 * developer pages' result object has both under different names. A status check
 * would make one of those callers lie.
 *
 * Deliberately NOT folded into STEP_UP_ERROR_CODES — see the note above
 * TWO_FACTOR_NOT_ENROLLED_CODE.
 */
export const isOperatorTwoFactorSetupRequired = (error: unknown): boolean => {
  const code = getErrorCode(error);
  return (
    code === ADMIN_TWO_FACTOR_SETUP_REQUIRED_CODE ||
    code === TWO_FACTOR_NOT_ENROLLED_CODE
  );
};

/**
 * A refusal the caller's own step-up UI answers in place: the code field for a
 * missing or wrong code, the set-up prompt (OperatorTwoFactorGate) for an operator
 * with no factor. A service that toasts its errors skips these, or the operator
 * gets a red toast on top of the prompt that already explains it.
 */
export const isStepUpAnsweredInPlace = (error: unknown): boolean =>
  isStepUpErrorCode(getErrorCode(error)) ||
  isOperatorTwoFactorSetupRequired(error) ||
  // A staff-confirmation failure is answered by the dialog that asked for it:
  // always when it is the office's own (nothing was sent), and for the api's
  // codes only while Confirm with Google is on. With it OFF, an api error that
  // happens to share a code (RATE_LIMITED, SESSION_ENDED) is surfaced as before.
  isStaffConfirmationError(error) ||
  (isStaffConfirmationOn() && isStaffConfirmationAnswer(error));

/** True when the server has locked the operator's codes for a while. */
export const isStepUpTooManyAttempts = (error: unknown): boolean =>
  getErrorCode(error) === TWO_FACTOR_TOO_MANY_ATTEMPTS_CODE;

/**
 * A code that was right but already spent. The api accepts each TOTP code once
 * per operator (utils/totp.ts replay key), across enrolment verify and every
 * step-up alike, and says so as INVALID_TWO_FACTOR_CODE with
 * `details.reason: "already_used"`. So the code that just finished enrolment, or
 * the one that just sent the previous queued action, is refused until the app
 * shows the next one. Telling the operator the code "didn't work" would send them
 * to retype the same digits.
 */
export const isStepUpCodeAlreadyUsed = (error: unknown): boolean => {
  if (!isStepUpCodeInvalid(error)) return false;
  const details = (error as { details?: unknown }).details;
  return (
    typeof details === "object" &&
    details !== null &&
    (details as { reason?: unknown }).reason === "already_used"
  );
};

/**
 * How a request wants step-up refusals handled, carried on the axios request
 * config (NEVER as a header — it must not reach the wire).
 *
 * - `"host"`: the caller has no code field of its own, so a set-up-required or
 *   TWO_FACTOR_REQUIRED answer is handed to the OperatorTwoFactorPromptHost, which
 *   prompts once and re-sends the identical request with X-2FA-Code. Opt-in, per
 *   call site (option B in ENG-1378): the ~30 `useStepUp` / `StepUpCodeField` /
 *   `useTwoFactorAction` callers handle these answers themselves and must never
 *   see a second prompt, so the default is "untouched".
 * - `"resend"`: this IS the host's re-send. Its answer goes back to the host, never
 *   to the host again, so set-up can never loop.
 */
export type StepUpHandling = "host" | "resend";

/** Every refused request passes here: an api asking for a confirmation is armed. */
export const noteIfConfirmationDemanded = (refusal: unknown): void => {
  if (isStaffConfirmationRequired(refusal)) noteConfirmationDemanded();
};

/**
 * A refusal the prompt host can answer: a code or set-up asked for, or, while
 * "Confirm with Google" is on, a staff confirmation asked for. With the controls
 * OFF this is exactly the old pair of checks.
 */
export const isHostAnswerableRefusal = (refusal: unknown): boolean =>
  isOperatorTwoFactorSetupRequired(refusal) ||
  isStepUpRequired(refusal) ||
  (isStaffConfirmationOn() && isStaffConfirmationRequired(refusal));
